import { formatHours } from '@/views/Projects/Engagement/engagement.js';

// Engagement changes logged on the project (properties.engagement_event): the sentence of the project history,
// the badge on the avatar and the details (US 9438, moved from EngagementActivity.vue)

const ACTIONS = {
  step_status: 'status',
  budget_updated: 'updated',
  time_logged: 'created',
  time_deleted: 'deleted',
  team_member_added: 'linked',
  team_member_removed: 'unlinked',
  document_added: 'uploaded',
  document_removed: 'deleted',
  document_signed_off: 'signed',
  document_rejected: 'status',
  document_renamed: 'updated',
  objective_deleted: 'deleted',
  objective_status_changed: 'status',
  test_sent_back: 'status',
  test_status_changed: 'status',
  test_duplicated: 'created',
  data_collection_reopened: 'status',
  working_paper_added: 'uploaded',
  evidence_linked: 'linked',
  evidence_unlinked: 'unlinked',
  annotation_added: 'created',
  annotation_deleted: 'deleted',
  accepted: 'signed',
  acceptance_withdrawn: 'status'
};

// Test results as coloured pills
const STATUS_TONES = {
  passed: 'green',
  failed: 'red',
  pending: 'grey',
  documentation_collection_completed: 'blue'
};

const ASSIGNEE_KEYS = ['add_main', 'remove_main', 'add_data_collection', 'remove_data_collection'];

function message(vm, event, p) {
  const key = 'projects.engagement.history.' + event;
  const objectiveStatus = status => status ? vm.$t('testing.status.' + vm.$options.filters.slugify(status)) : '–';
  switch (event) {
    case 'step_status':
      return vm.$t(key, { step: vm.$t('projects.engagement.step.' + p.step), status: vm.$t('projects.engagement.status.' + p.status) });
    case 'steps_included':
      return vm.$t(key, { steps: (p.steps || []).map(step => vm.$t('projects.engagement.step.' + step)).join(', ') });
    case 'budget_updated':
      return vm.$t(key, { budget: Object.keys(p.budget || {}).map(phase => vm.$t('projects.engagement.phase_short.' + phase) + ' ' + formatHours(p.budget[phase]) + ' h').join(', ') });
    case 'time_logged':
    case 'time_deleted':
      return vm.$t(key, { hours: formatHours(p.hours), user: p.user, date: vm.$options.filters.formatDate(p.entry_date), phase: vm.$t('projects.engagement.phase_short.' + p.phase) });
    case 'team_member_added':
    case 'team_role_changed':
      return vm.$t(key, { user: p.user, role: vm.$t('projects.engagement.role.' + p.role) });
    case 'team_member_removed':
      return vm.$t(key, { user: p.user, count: p.removed_assignments || 0 });
    case 'document_added':
    case 'document_removed':
    case 'document_signed_off':
    case 'document_rejected':
      return vm.$t(key, { file: p.file_name });
    case 'objective_updated':
    case 'objective_deleted':
      return vm.$t(key, { objective: p.objective });
    case 'objectives_reordered':
      return vm.$t(key, { objectives: (p.objectives || []).join(', ') });
    case 'objective_status_changed':
      return vm.$t(key, { objective: p.objective, status: objectiveStatus(p.status) });
    case 'test_sent_back':
      return vm.$t(key, { test: p.test, task: p.task });
    case 'data_collection_reopened':
      return vm.$t(key, { task: p.task });
    case 'test_duplicated':
      return vm.$t(key, { test: p.test, source: p.source_test, task: p.task });
    case 'test_status_changed':
      return vm.$t(key, { test: p.test, task: p.task, status: vm.$t('testing.status.' + p.status) });
    case 'working_paper_added':
      return vm.$t(key, { file: p.file_name, task: p.task });
    case 'evidence_linked':
      return vm.$t(key, { file: p.file_name, tasks: (p.tasks || []).join(', ') });
    case 'evidence_unlinked':
      // Unlinking also deletes the annotations made on the task
      return vm.$t(key, { file: p.file_name, tasks: (p.tasks || []).join(', ') })
        + (p.annotations_removed ? ' – ' + vm.$t('projects.engagement.history.annotations_removed', { count: p.annotations_removed }) : '');
    case 'annotation_added':
    case 'annotation_updated':
    case 'annotation_deleted':
    case 'annotation_made_general':
      return vm.$t(key, { file: p.file_name, task: p.task || vm.$t('annotations.general') });
    case 'document_renamed':
      return vm.$t(key, { file: p.file_name, old: p.old_file_name });
    default:
      return vm.$t(key);
  }
}

export function describeEngagement(vm, activity) {
  const p = activity.properties || {};
  const event = p.engagement_event;
  const entry = {
    action: ACTIONS[event] || (/_added$/.test(event) ? 'linked' : (/_(deleted|removed)$/.test(event) ? 'deleted' : 'updated')),
    sentence: { text: message(vm, event, p) }
  };
  // The new test result as a coloured pill in the sentence
  if (event === 'test_status_changed' && p.status) {
    entry.sentence = {
      path: 'projects.engagement.history.' + event,
      values: { test: p.test, task: p.task },
      pill: { place: 'status', tone: STATUS_TONES[p.status] || 'grey', text: vm.$t('testing.status.' + p.status) }
    };
  }
  // Bulk assignee changes: who was added to or removed from the tasks
  if (event === 'task_assignees_updated') {
    entry.changes = [
      { key: 'main', label: vm.$t('activity_log.fields.main_tasks'), kind: 'list', added: p.add_main || [], removed: p.remove_main || [] },
      { key: 'data_collection', label: vm.$t('activity_log.fields.data_collection_tasks'), kind: 'list', added: p.add_data_collection || [], removed: p.remove_data_collection || [] }
    ].filter(change => change.added.length || change.removed.length);
    if (!entry.changes.length && ASSIGNEE_KEYS.every(key => !(p[key] || []).length)) {
      entry.notes = [vm.$t('activity_log.team_defaults_changed')];
    }
  }
  // The project the change was made in, for lists across projects
  if (activity.subject_type === 'App\\Project\\Models\\Project' && activity.subject && activity.subject.title) {
    entry.context = activity.subject.title;
  }
  return entry;
}
